import Store from '@ember-data/store';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { SelectOption } from 'donor-elf-web/lib/form-component-utils';
import CampaignModel from 'donor-elf-web/models/campaign';
import CampaignLineItemModel from 'donor-elf-web/models/campaign-line-item';
import HostCampaignModel from 'donor-elf-web/models/host-campaign';
import LineItemModel from 'donor-elf-web/models/line-item';
import CampaignService from 'donor-elf-web/services/campaign-service';
import Changeset from 'ember-changeset';

interface ComponentArgs {
  lineItem: LineItemModel;
}

interface CampaignLineItemInfo {
  campaign?: CampaignModel;
  hostCampaign?: HostCampaignModel;
  approved: boolean;
  amount: number;
}

export default class LineItemCampaignComponent extends Component<ComponentArgs> {
  @service declare campaignService: CampaignService;
  @service declare store: Store;

  // used for creating a new campaign line item
  @tracked newCampaignLineItem?: CampaignLineItemModel;

  @tracked editing = false;
  @tracked changeset: any;
  @tracked showValidationErrors = false;
  @tracked saving = false;
  @tracked saveError?: any;

  get campaignLineItem(): CampaignLineItemModel | undefined {
    return (
      this.args.lineItem.campaignLineItemsArray[0] || this.newCampaignLineItem
    );
  }

  // currently only handling one campaign per line item
  get campaignLineItemInfo(): CampaignLineItemInfo | undefined {
    let { campaignLineItem } = this;
    if (campaignLineItem) {
      return {
        campaign:
          this.campaignService.campaignsById[campaignLineItem.campaignId!],
        hostCampaign:
          this.campaignService.hostCampaignsById[
            campaignLineItem.hostCampaignId!
          ],
        approved: campaignLineItem.approved,
        amount: campaignLineItem.amount,
      };
    } else {
      return undefined;
    }
  }

  createChangeset() {
    let { campaignLineItem } = this;
    if (!campaignLineItem) {
      this.newCampaignLineItem = campaignLineItem = this.store.createRecord(
        'campaign-line-item',
        {
          lineItemId: this.args.lineItem.id,
          amount: this.args.lineItem.amount,
        },
      );
    }
    this.changeset = new Changeset(campaignLineItem!);
    // always approve the campaign line item when a user saves it
    this.changeset.set('approved', true);
  }

  get campaignSelectOptions(): SelectOption[] {
    return [
      { val: undefined, label: 'None' },
      ...this.campaignService.sortedNonRestrictedCampaigns.map((campaign) => {
        return { val: campaign.id, label: campaign.label };
      }),
    ];
  }

  get associatedWithRestrictedCampaign() {
    return this.editing && this.campaignLineItemInfo?.campaign?.restricted;
  }

  @action
  setEditing(editing: boolean) {
    if (editing) {
      this.createChangeset();
      this.showValidationErrors = false;
      this.saving = false;
      this.saveError = undefined;
    }
    this.editing = editing;
  }

  @action
  async save() {
    if (this.saving) {
      return;
    }
    this.showValidationErrors = false;
    await this.changeset.validate();
    if (this.changeset.get('isValid')) {
      try {
        this.saving = true;
        this.editing = false;
        await this.changeset.save();
        if (this.newCampaignLineItem) {
          await this.args.lineItem.reload();
          this.newCampaignLineItem = undefined;
        }
      } catch (e) {
        this.editing = true;
        this.saveError = e;
      }
      this.saving = false;
    } else {
      this.showValidationErrors = true;
    }
  }
}
